import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { push } from "connected-react-router";
import {
	Modal,
	Button,
	ProgressIndicator,
	ReduxControls,
	EmptyItem,
	noop
} from "rhombus-react";

import {
	setVASTUploadList,
	uploadVASTAudioCreative,
	encodeVASTAudioCreatives,
	encodeVastAudioGrowls,
	uploadAudioImposiumCreative,
	encodeAudioImposiumCreative,
	encodeAudioImposiumGrowls
} from "reducers/creatives/VAST";
import { addNotification } from "reducers/Notifications";
import { loadCreatives } from "reducers/Creatives";

import SelectAssets from "widgets/modals/assets/SelectAssets";

import useCancelToken from "hooks/useCancelToken";

import { useModalStack, formatBytes, getUploadFiles } from "utils/common";
import {
	UPLOAD_AUDIO_ASSET_MAX_BYTES,
	UPLOAD_AUDIO_ACCEPTED_FORMATS,
	UPLOAD_AUDIO_CREATIVE,
	AUDIO_NON_DYNAMIC,
	AUDIO_IMPOSIUM,
	AUDIO_BUILD_METHOD_LABELS
} from "utils/constants";

import VASTUploadList from "./VASTUploadList";
import SkipModal from "./SkipModal";
import ImposiumAudioMultiCreativeModal from "widgets/modals/imposium/ImposiumAudioMultiCreativeModal";
import CreateGeneratedAudioModal from "./CreateGeneratedAudioModal";

import './CreateAudio.css';

export default function CreateAudio(props) {
	const { returnPath, libraryId, creativeId, isMultiple } = props;
	const dispatch = useDispatch();
	let modalTitle = "Create Audio";

	if (creativeId) {
		modalTitle = "Select New Audio";
	}

	const commonModalProps = {
		title: modalTitle,
		type: "modal",
		className: "create-audio-modal",
		xClose: true,
		allowFullScreen: false,
		bladeProps: {
			verticallyCenter: true,
		},
		onClose: closeModal,
		setAppTitle: false,
	};

	const [, { pushModal }, ModalStackHead] = useModalStack({ commonModalProps });

	const { isLoaded, isAudioGenerationEnabled, isAdvertiserDetailsLoaded } = useSelector(state => ({
		isLoaded: state.libraryProperties?.isLoaded,
		isAudioGenerationEnabled: state.advertiserDetails?.data?.aiGeneration?.isAudioGenerationEnabled,
		isAdvertiserDetailsLoaded: state.advertiserDetails?.isLoaded,
	}));

	const { token: cancelToken, cancel } = useCancelToken();
	const [isUploading, setIsUploading] = useState(false);
	const [dynamicValue, setDynamicValue] = useState(AUDIO_NON_DYNAMIC);
	const isImposiumSelected = dynamicValue === AUDIO_IMPOSIUM;
	let replaceCreative = false;

	const formattedMaxSize = formatBytes(UPLOAD_AUDIO_ASSET_MAX_BYTES);

	function closeModal() {
		cancel("Uploads canceled.");
		dispatch(push(returnPath));
	}

	if (!isMultiple && creativeId) {
		replaceCreative = { id: creativeId };
	}

	function openSelect() {
		function Configured(props) {
			const modalProps = {
				...props,
				commonModalProps: {
					...props.commonModalProps,
					title: `${modalTitle} - ${AUDIO_BUILD_METHOD_LABELS[dynamicValue]}`,
				},
				type: "audio",
				multiple: isMultiple && !isImposiumSelected,
				submitButtonText: "Create Audio Creative",
				submitButtonTheme: "green",

				onSelect: (selected) => {
					if (isImposiumSelected) {
						return dispatch(encodeAudioImposiumCreative(libraryId, selected))
							.then((response) => {
								closeModal();
							});
					}

					return dispatch(encodeVASTAudioCreatives(libraryId, selected, replaceCreative))
						.then(closeModal);
				},
				accept: UPLOAD_AUDIO_ACCEPTED_FORMATS,
			};

			return <SelectAssets {...modalProps} />;
		}

		pushModal(Configured);
	}

	function openGenerate(){
		function Configured(props){
			let modalProps = {
				...props,
				libraryId,
				closeModal,
			};
			return <CreateGeneratedAudioModal {...modalProps} />;
		}

		pushModal(Configured);
	}

	function openSkip(file, message, onSkip = noop) {
		function Configured(props) {
			let modalProps = {
				...props,

				file,
				message,
				onSkip,
			};
			return <SkipModal {...modalProps} />;
		}

		pushModal(Configured);
	}

	function openImposiumAudioProject() {
		function Configured(props) {
			const modalProps = {
				...props,
				onClose: closeModal,
			};

			return <ImposiumAudioMultiCreativeModal {...modalProps} libraryId={libraryId} />;
		}

		pushModal(Configured);
	}

	function onUpload(e) {
		let fileList = e.target.files;

		dispatch(setVASTUploadList(getUploadFiles(fileList)));
		setIsUploading(true);
		let numSuccessful = 0;

		function uploadFile(itemNum) {
			let file = fileList.item(itemNum);

			if (!file) {
				if (numSuccessful > 0) {
					dispatch(addNotification({
						message: isImposiumSelected ? encodeAudioImposiumGrowls(numSuccessful) : encodeVastAudioGrowls(numSuccessful),
						type: "success",
					}));
					dispatch(loadCreatives(libraryId));
				}
				closeModal();
				return;
			}

			if (file.size > UPLOAD_AUDIO_ASSET_MAX_BYTES) {
				let description = `Cannot upload file "${file.name}" because it exceeds the maximum file size of ${formattedMaxSize}.`;
				openSkip(file, description, () => { uploadFile(itemNum + 1); });
				return;
			}

			if (isImposiumSelected) {
				dispatch(uploadAudioImposiumCreative(libraryId, fileList[0], cancelToken))
					.then((resp) => {
						if (resp.isCanceled) {
							closeModal();
							return;
						}
						if (resp.success) {
							const { creatives = []} = resp.payload?.data || {};
							if (creatives.length) {
								dispatch(addNotification({
									message: encodeAudioImposiumGrowls(creatives.length),
									type: "success",
								}));
								dispatch(loadCreatives(libraryId));
							}
							closeModal();
						} else {
							let { description = `Cannot upload file ${file.name}` } = (resp?.payload?.errorObj || {});
							openSkip(file, description, () => { closeModal(); });
						}
					});
				return;
			}

			dispatch(uploadVASTAudioCreative(libraryId, file, cancelToken, replaceCreative))
				.then((resp) => {
					if (resp.isCanceled) {
						closeModal();
						return;
					}
					if (resp.success) {
						/*
							The response is "successful" even when errors are sent back for a file upload.
							We are submitting uploads sequentially, so errors will always be in the 0 index
							as opposed to matching `itemNum`.

							However, even with an error, we only want to skip the creative if it does not exist
							in the creatives payload.
						*/
						const { creatives = [], errors = []} = resp.payload?.data || {};

						if (!creatives.length && errors.length) {
							const [errorObj] = errors;
							const description = errorObj.errors.map(errorObj => errorObj.error).join('');
							openSkip(file, description, () => { uploadFile(itemNum + 1); });
							return;
						}

						numSuccessful += 1;
						uploadFile(itemNum + 1);
					} else {
						let { description = `Cannot upload file ${file.name}` } = (resp?.payload?.errorObj || {});
						openSkip(file, description, () => { uploadFile(itemNum + 1); });
					}
				});
		}

		uploadFile(0);
	}

	function Body() {
		if (isUploading) {
			return <VASTUploadList />;
		}

		const headerContent = () => {
			const handleChange = (e) => {
				setDynamicValue(e.target.value);
			};

			if (!isAdvertiserDetailsLoaded) {
				return (
					<header className="header-info-audio">
						<div className="text-container">
							<strong>Select how you would like to add audio</strong>
							<p>Accepted file types: {UPLOAD_AUDIO_ACCEPTED_FORMATS}</p>
						</div>
					</header>
				);
			}

			return (
				<header className="header-info-audio with-build-methods">
					<strong>Select build method</strong>
					<div className="build-methods">
						<ReduxControls.Checkbox
							name="nonDynamicAudio"
							label={
								<>
									<span>Standard</span>
									<span>Audio</span>
								</>
							}
							type="radio"
							value={AUDIO_NON_DYNAMIC}
							checked={dynamicValue === AUDIO_NON_DYNAMIC}
							onChange={handleChange}
						/>
						<ReduxControls.Checkbox
							name="imposiumAudio"
							className="cypress-imposium-audio-build-method"
							label={AUDIO_BUILD_METHOD_LABELS[AUDIO_IMPOSIUM]}
							type="radio"
							value={AUDIO_IMPOSIUM}
							checked={isImposiumSelected}
							onChange={handleChange}
						/>
					</div>
				</header>
			);
		};

		let selectionHeader = "Select how you would like to add audio";
		let uploadHeader = "Upload Audio";
		let uploadMessage = `(${formattedMaxSize} max file size per audio)`;
		let chooseExistingHeader = "Choose Existing Audio";

		if (isImposiumSelected) {
			selectionHeader = "Select from below to create an Imposium creative";
		}

		return (
			<div className="body-container">
				<ProgressIndicator key="loading" isLoading={!isLoaded} />
				{headerContent()}

				{isAdvertiserDetailsLoaded && (
					<div className="selection-text">
						<strong>{selectionHeader}</strong>
						<p>Accepted file types: {UPLOAD_AUDIO_ACCEPTED_FORMATS}</p>
					</div>
				)}

				<div className="selection-routes">
					{isImposiumSelected && (
						<div
							className="cypress-imposium-audio-open-blank-imposium"
							onClick={openImposiumAudioProject}>
							<EmptyItem
								icon="fal fa-file-plus"
								title="Imposium Project"
								message=" "
							/>
						</div>
					)}

					<label htmlFor={UPLOAD_AUDIO_CREATIVE} className="upload-audio">
						<EmptyItem
							icon="fal fa-cloud-upload"
							title={uploadHeader}
							message={uploadMessage}
						/>
					</label>

					<div className="open-select" onClick={openSelect}>
						<EmptyItem
							className="choose-existing"
							icon="fal fa-folder-open"
							title={chooseExistingHeader}
							message=" "
						/>
					</div>

					{isAudioGenerationEnabled && (
						<div className="open-generate" onClick={openGenerate}>
							<EmptyItem
								icon="fal fa-wand-magic-sparkles"
								title="Generate Audio"
								message=" "
							/>
						</div>
					)}
				</div>
				<input
					type="file"
					multiple={isMultiple && !isImposiumSelected}
					id={UPLOAD_AUDIO_CREATIVE}
					className="hidden"
					accept={UPLOAD_AUDIO_ACCEPTED_FORMATS}
					onChange={onUpload}
				/>
			</div>
		);
	}

	function Footer() {
		return (
			<Button
				theme="default"
				type="reset"
				onClick={closeModal}
			>
				Cancel
			</Button>
		);
	}

	if (ModalStackHead) {
		return <ModalStackHead />;
	}

	return (
		<Modal
			{...commonModalProps}
			renderContent={Body}
			renderFooter={Footer}
		/>
	);
}

CreateAudio.displayName = "CreateAudio";
CreateAudio.defaultProps = {
	libraryId: -1,
	creativeId: null,
	returnPath: "",
};
